import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Search, ChevronRight, Sun, Moon, Bell } from "lucide-react";
import { cn } from "../ui/cn";
import { Kbd } from "../ui/Badge";
import { useAuth } from "../auth/AuthContext";
import { useColorMode } from "../App";
import { apiGet, apiDelete } from "../api/client";
import NotificationsDrawer from "./NotificationsDrawer";

const ACTIVITY_LAST_SEEN_KEY = "bidrl_activity_last_seen";
const ACTIVITY_POLL_MS = 30_000;

const PATH_LABELS = {
  "": "Dashboard",
  "sales": "Sales",
  "inventory": "Inventory",
  "unfulfilled-sales": "Unfulfilled Sales",
  "manual-updates": "Manual Updates",
  "upcoming": "Upcoming Auctions",
  "scheduled-bids": "Scheduled Bids",
  "tasks": "Tasks",
  "returns": "Returns",
  "listings": "eBay Listings",
  "shipments": "Shipments",
  "processing": "Processing",
  "processing-pallets": "Pallets",
  "pokimane": "Pokimane",
  "settings": "Settings",
};

function buildCrumbs(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  const adminIdx = parts.indexOf("admint");
  const trail = adminIdx >= 0 ? parts.slice(adminIdx + 1) : parts;
  if (trail.length === 0) return [{ label: "Dashboard", href: "/admint/" }];
  const crumbs = [{ label: "Dashboard", href: "/admint/" }];
  let acc = "/admint";
  for (const seg of trail) {
    acc += `/${seg}`;
    crumbs.push({ label: PATH_LABELS[seg] || seg, href: acc });
  }
  return crumbs;
}

export default function TopBar({ onOpenPalette }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode, toggle: toggleColorMode } = useColorMode();
  const [, setNow] = useState(() => new Date());

  // Activity feed — polled every 30s. ``lastSeenAt`` is an epoch-second
  // marker stored in localStorage; entries newer than it bump the badge.
  // The marker is advanced when the user opens the drawer so they don't
  // re-see the same notifications across tabs/sessions.
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState(() => {
    try { return Number(localStorage.getItem(ACTIVITY_LAST_SEEN_KEY) || 0); }
    catch { return 0; }
  });
  // Keep the badge frozen while the drawer is open — when the user closes
  // it, advance the marker so the badge clears.
  const drawerOpenedAt = useRef(0);

  const refreshActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const res = await apiGet("/api/activity?limit=200");
      setActivity(Array.isArray(res?.entries) ? res.entries : []);
    } catch {
      // Silent — activity is non-critical, no toast spam.
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshActivity();
    const id = window.setInterval(refreshActivity, ACTIVITY_POLL_MS);
    const onFocus = () => refreshActivity();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshActivity]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const crumbs = buildCrumbs(location.pathname);
  const initial = (user?.username || "?").charAt(0).toUpperCase();

  // Unread = entries newer than the persisted marker, capped to 99 for the badge.
  const unread = activity.filter((e) => Number(e.ts) > Number(lastSeenAt || 0)).length;
  const unreadLabel = unread > 99 ? "99+" : String(unread);

  const openDrawer = () => {
    drawerOpenedAt.current = Math.floor(Date.now() / 1000);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Advance the seen marker to whatever was the newest entry at open time
    // so anything that arrived during the session stays unread.
    const newest = activity.length ? Number(activity[0].ts) : Math.floor(Date.now() / 1000);
    const next = Math.max(newest, drawerOpenedAt.current || 0);
    setLastSeenAt(next);
    try { localStorage.setItem(ACTIVITY_LAST_SEEN_KEY, String(next)); } catch { /* ignore */ }
  };

  const clearActivity = async () => {
    try {
      await apiDelete("/api/activity");
      setActivity([]);
    } catch { /* silent */ }
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-30 h-14 px-4 flex items-center gap-3",
        "bg-bg-surface/85 backdrop-blur-xl border-b border-border"
      )}
    >
      <nav
        aria-label="Breadcrumb"
        className="flex-1 min-w-0 flex items-center gap-1.5 text-sm text-ink-muted overflow-hidden"
      >
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <React.Fragment key={c.href + i}>
              {i > 0 && <ChevronRight size={14} className="text-ink-subtle shrink-0" />}
              {last ? (
                <span className="text-ink font-medium truncate">{c.label}</span>
              ) : (
                <Link to={c.href} className="hover:text-ink transition-colors truncate">
                  {c.label}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      <button
        type="button"
        onClick={onOpenPalette}
        className={cn(
          "hidden md:inline-flex items-center gap-2 h-8 px-2.5 rounded-lg",
          "bg-bg-subtle border border-border text-ink-muted text-xs",
          "hover:bg-bg hover:border-border-strong hover:text-ink transition-colors"
        )}
      >
        <Search size={14} />
        <span>Search</span>
        <span className="flex items-center gap-0.5">
          <Kbd>⌘</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>

      <button
        type="button"
        onClick={openDrawer}
        className="relative inline-flex items-center justify-center h-8 w-8 rounded-lg text-ink-muted hover:bg-bg-subtle hover:text-ink transition-colors"
        aria-label={unread > 0 ? `${unread} unread notifications` : "Notifications"}
        title="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full grid place-items-center text-[10px] font-semibold text-white bg-brand-500 leading-none"
            aria-hidden
          >
            {unreadLabel}
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={toggleColorMode}
        className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-ink-muted hover:bg-bg-subtle hover:text-ink transition-colors"
        aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        title={mode === "dark" ? "Light mode" : "Dark mode"}
      >
        {mode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
      </button>

      <button
        type="button"
        onClick={() => navigate("/admint/settings")}
        className="inline-flex items-center gap-2 pr-1 pl-1 h-9 rounded-lg hover:bg-bg-subtle transition-colors"
        aria-label="Open settings"
      >
        <span className="h-8 w-8 rounded-full bg-brand-500 grid place-items-center text-white text-sm font-semibold">
          {initial}
        </span>
      </button>

      <NotificationsDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        entries={activity}
        lastSeenAt={lastSeenAt}
        onClear={clearActivity}
        onRefresh={refreshActivity}
        refreshing={activityLoading}
      />
    </header>
  );
}
